/**
 * Groups hub (#/groups) — visible group cards + share chrome + modals.
 * Moved from overview.route.js ov-legacy-ops (UI-DEAD-2b).
 */

import { escapeHtml } from "./dom.js";
import {
  overviewGroupSlotsTruncatedHtml,
  mergeTenantOwnerDirectory,
  buildOverviewSlotsFromApiResponse,
  mergeSettingsOnlyOverviewSlots,
  coalesceOverviewGroupSlots,
  enrichOverviewSlotsForDisplay,
  filterOverviewSlotsByOwner,
  syncOverviewGroupSlotsTruncBanner,
} from "./overview-group-slots.js";
import { overviewFleetPollTruncatedHtml } from "./overview-fleet-poll.js";
import {
  groupDeviceIdsFromList,
  inferUniqueOwnerForGroup,
  groupDeviceIdsFromSlot,
  sirenTargetIdsForSlot,
  canGroupSirenForSlot,
  groupSharedBySlot,
  groupSharedByNotificationKey,
} from "./overview-group-slot-helpers.js";
import {
  resolveGroupAlarmOwnerAdmin,
} from "./overview-group-slots.js";
import { createOverviewGroupModalsController, readOverviewGroupSlotFromBtn } from "./overview-group-modals.js";
import { createOverviewShareChrome } from "./overview-share-modal.js";
import { buildOverviewGroupCardHtml, buildOverviewGroupDeviceRowHtml } from "./overview-group-card-html.js";
import {
  fetchOverviewLivePair,
  OVERVIEW_LIVE_MS,
  syncOverviewFleetPollTruncBanner,
} from "./overview-fleet-poll.js";
import {
  assessOverviewLiveTick,
  syncOverviewLivePollDegradedBanner,
  shouldToastOverviewLivePollFail,
} from "./overview-live-poll-status.js";

/**
 * @param {object} opts
 * @returns {string}
 */
export function buildGroupsHubCardsSectionHtml(opts) {
  const showGranteeGroupsHubLink = !!opts.showGranteeGroupsHubLink;
  const showShareChrome = !!opts.showShareChrome;
  const me = opts.me || null;
  const ov = opts.ov || null;
  const list = opts.list || { items: [] };
  return `
    ${overviewGroupSlotsTruncatedHtml(ov, "ghGroupSlotsTruncBanner")}
    ${overviewFleetPollTruncatedHtml(list, ov, "ghFleetTruncBanner")}
    <section class="card card--groups grp-hub-cards-section">
      <div class="row">
        <h2 style="margin:0">Group cards</h2>
        <button class="btn sm secondary right" id="grpNew">New group</button>
      </div>
      ${showGranteeGroupsHubLink ? `<p class="muted" style="margin:0 0 8px;font-size:12px">Shared groups — read-only list (sidebar <strong>Groups</strong>).</p>` : ""}
      ${me && me.role === "superadmin" ? `
      <div class="row" style="flex-wrap:wrap;gap:10px;align-items:flex-end;margin:10px 0 4px">
        <label class="field" style="margin:0;min-width:220px;flex:1">
          <span>Find by account</span>
          <input type="search" id="ghOwnerFilter" list="ghOwnerDatalist" placeholder="Account name…" autocomplete="off" />
          <datalist id="ghOwnerDatalist"></datalist>
        </label>
        <button type="button" class="btn sm secondary btn-tap" id="ghOwnerClear">Clear</button>
      </div>
      <p class="muted" style="margin:0 0 8px;font-size:12px">One card per account group. Groups without an owner show as “Unowned”.</p>
      ` : ""}
      <div id="groupCards" class="device-grid"></div>
      ${showShareChrome ? `
      <details class="share-fold" id="grpShareFold">
        <summary class="share-fold__summary">
          <span>Group sharing</span>
        </summary>
        <div class="share-global-panel">
          <div class="share-global-head">
            <div class="share-global-toolbar">
              <button class="btn sm secondary btn-tap" type="button" id="grpShareRefresh">Refresh</button>
              <a href="#/group/_all" class="btn sm secondary btn-tap" id="grpShareFleetLink" title="Open all boards">All boards</a>
              <button class="btn sm btn-tap" type="button" id="grpShareOpen">New share</button>
            </div>
          </div>
          <p id="grpShareStatLine" class="muted" style="margin:0 0 6px;font-size:12px" aria-live="polite"></p>
          <p class="muted" style="margin:0 0 6px;font-size:12px">Access is granted <strong>per board</strong>. The list below shows who can see or operate shared boards. Use group cards above for day-to-day alarm work.</p>
          <p class="muted" style="margin:0 0 6px;font-size:12px">People with <strong>Operate</strong> on every board in a card may edit that card’s alarm timing.</p>
          <details style="margin-top:8px;border:1px solid var(--border, #2a3340);border-radius:8px;padding:6px 10px">
            <summary class="muted" style="cursor:pointer;font-size:12px;user-select:none">Who has access — <button type="button" class="btn sm ghost btn-tap" data-cc-go="audit-log?source=audit&action=notification_group.share">Share history</button></summary>
            <div id="shareGrantsTableWrap" class="share-grants-table mini" style="margin-top:10px">
              <p class="muted" style="margin:0;padding:8px 0">Loading shares…</p>
            </div>
          </details>
        </div>
      </details>` : ""}
    </section>`;
}

/**
 * Edit-group + trigger-settings modals for a single #/group/:key page.
 * Omits shareModal (group detail already has Add Members / share chrome).
 * @returns {string}
 */
export function buildGroupDetailEditModalHtml() {
  return `
    <div id="grpSetModal" class="grp-modal" style="display:none">
      <div class="grp-modal-card">
        <h3 style="margin:0 0 8px">Group trigger settings</h3>
        <p class="muted" id="gsKeyLabel" style="margin:0 0 10px"></p>
        <p class="muted" style="margin:0 0 10px;font-size:12px;line-height:1.45">Delay 0 = siren starts right away. Duration is how long the siren stays on (minutes).</p>
        <label class="field"><span>Siren duration (minutes)</span><input id="gsDurMin" type="number" min="0.5" max="5" step="0.5" /></label>
        <label class="field field--spaced"><span>Delay before siren (seconds)</span><input id="gsDelay" type="number" min="0" max="3600" step="1" /></label>
        <label class="field field--spaced field--toggle">
          <span class="row field--toggle__row" style="margin:0;align-items:flex-start;gap:10px">
            <input id="gsReboot" type="checkbox" />
            <span class="field--toggle__text">Reboot + self-check this group after trigger</span>
          </span>
        </label>
        <div class="row" style="justify-content:flex-end;gap:8px;margin-top:10px">
          <button class="btn sm secondary" id="gsCancel" type="button">Cancel</button>
          <button class="btn sm secondary" id="gsApply" type="button">Apply now</button>
          <button class="btn sm" id="gsSave" type="button">Save</button>
        </div>
      </div>
    </div>
    <div id="grpModal" class="grp-modal" style="display:none">
      <div class="grp-modal-card grp-modal-card--edit">
        <header class="grp-modal__head">
          <h3 class="grp-modal__title">Edit group</h3>
          <p class="grp-modal__lede muted">Name, contacts, then tick boards. Save updates membership for checked rows.</p>
        </header>
        <div class="grp-modal__fields">
          <label class="field"><span>Group name</span><input id="gmKey" placeholder="e.g. Warehouse A" autocomplete="off"/></label>
          <p class="muted grp-modal__key-hint" style="margin:-2px 0 10px;font-size:11px;line-height:1.45">Spaces tidy on save. <strong>Card title</strong> below is optional — leave blank to use the group name. Tick boards to include them in this group.</p>
          <label class="field"><span>Card title (optional)</span><input id="gmName" autocomplete="off" placeholder="Shown on the card"/></label>
          <label class="field"><span>Contact name</span><input id="gmOwner" autocomplete="name"/></label>
          <label class="field"><span>Phone</span><input id="gmPhone" inputmode="tel" autocomplete="tel"/></label>
          <label class="field"><span>Email</span><input id="gmEmail" type="email" autocomplete="email"/></label>
          <div class="field field--devices">
            <span>Boards in this group</span>
            <span class="muted" style="font-size:11px;margin-top:-2px;line-height:1.35">Tick boards with no group or already in this one; <strong>Save</strong> applies. Boards already in another group stay locked until moved.</span>
            <div id="gmDevices" class="grp-device-picker" role="group" aria-label="Group member boards"></div>
          </div>
        </div>
        <div class="row grp-modal__actions" style="justify-content:flex-end;gap:8px;margin-top:12px">
          <button class="btn sm secondary" id="gmCancel" type="button">Cancel</button>
          <button class="btn sm" id="gmSave" type="button">Save</button>
        </div>
      </div>
    </div>`;
}

/** @returns {string} */
export function buildGroupsHubCardsModalsHtml() {
  return `
    <div id="shareModal" class="grp-modal" style="display:none">
      <div class="grp-modal-card" style="max-width:760px;width:min(760px,96vw)">
        <h3 style="margin:0 0 8px" id="shareModalTitle">Share boards / group</h3>
        <p class="muted" id="shareTargetHint" style="margin:0 0 10px">Select boards, users, and permissions.</p>
        <p class="muted" id="shareEditNote" style="margin:0 0 8px;display:none"></p>
        <div class="row" style="gap:10px;align-items:flex-start;flex-wrap:wrap">
          <div style="flex:1;min-width:280px">
            <div class="row" style="justify-content:space-between;align-items:center" id="shareDeviceListHead">
              <strong id="shareDeviceListTitle">Boards</strong>
              <label class="muted" id="shareSelAllDevicesLabel"><input type="checkbox" id="shareSelAllDevices" /> Select all</label>
            </div>
            <div id="shareDeviceList" class="grp-pick-list grp-pick-list--devices" style="max-height:280px;overflow:auto"></div>
          </div>
          <div style="flex:1;min-width:280px">
            <div class="row" style="justify-content:space-between;align-items:center">
              <strong>Users</strong>
              <label class="muted"><input type="checkbox" id="shareSelAllUsers" /> Select all</label>
            </div>
            <div id="shareUserList" class="grp-pick-list" style="max-height:260px;overflow:auto"></div>
          </div>
        </div>
        <div class="row" style="margin-top:10px;gap:14px;flex-wrap:wrap">
          <label><input type="checkbox" id="sharePermView" checked /> View</label>
          <label><input type="checkbox" id="sharePermOperate" /> Operate</label>
          <p class="muted" style="margin:8px 0 0;font-size:12px;line-height:1.45;flex-basis:100%">
            <strong>Alarm ON/OFF</strong> needs alert permission or Operate share.
            <strong>Settings / commands</strong> need Operate on every board in the group.
          </p>
        </div>
        <p class="muted" id="shareBatchStat" style="min-height:1.2em;margin:8px 0 0"></p>
        <div class="row" style="justify-content:flex-end;gap:8px;margin-top:10px">
          <button class="btn sm secondary" id="shareModalRetryGrantees" type="button" style="display:none">Retry loading members</button>
          <button class="btn sm secondary" id="shareModalCancel" type="button">Cancel</button>
          <button class="btn sm" id="shareModalApply" type="button">Apply sharing</button>
        </div>
      </div>
    </div>
    <div id="grpSetModal" class="grp-modal" style="display:none">
      <div class="grp-modal-card">
        <h3 style="margin:0 0 8px">Group trigger settings</h3>
        <p class="muted" id="gsKeyLabel" style="margin:0 0 10px"></p>
        <p class="muted" style="margin:0 0 10px;font-size:12px;line-height:1.45">Delay 0 = siren starts right away. Duration is how long the siren stays on (minutes).</p>
        <label class="field"><span>Siren duration (minutes)</span><input id="gsDurMin" type="number" min="0.5" max="5" step="0.5" /></label>
        <label class="field field--spaced"><span>Delay before siren (seconds)</span><input id="gsDelay" type="number" min="0" max="3600" step="1" /></label>
        <label class="field field--spaced field--toggle">
          <span class="row field--toggle__row" style="margin:0;align-items:flex-start;gap:10px">
            <input id="gsReboot" type="checkbox" />
            <span class="field--toggle__text">Reboot + self-check this group after trigger</span>
          </span>
        </label>
        <div class="row" style="justify-content:flex-end;gap:8px;margin-top:10px">
          <button class="btn sm secondary" id="gsCancel" type="button">Cancel</button>
          <button class="btn sm secondary" id="gsApply" type="button">Apply now</button>
          <button class="btn sm" id="gsSave" type="button">Save</button>
        </div>
      </div>
    </div>
    <div id="grpModal" class="grp-modal" style="display:none">
      <div class="grp-modal-card grp-modal-card--edit">
        <header class="grp-modal__head">
          <h3 class="grp-modal__title">Edit group</h3>
          <p class="grp-modal__lede muted">Name, contacts, then tick boards. Save updates membership for checked rows.</p>
        </header>
        <div class="grp-modal__fields">
          <label class="field"><span>Group name</span><input id="gmKey" placeholder="e.g. Warehouse A" autocomplete="off"/></label>
          <p class="muted grp-modal__key-hint" style="margin:-2px 0 10px;font-size:11px;line-height:1.45">Spaces tidy on save. <strong>Card title</strong> below is optional — leave blank to use the group name. Tick boards to include them in this group.</p>
          <label class="field"><span>Card title (optional)</span><input id="gmName" autocomplete="off" placeholder="Shown on the card"/></label>
          <label class="field"><span>Contact name</span><input id="gmOwner" autocomplete="name"/></label>
          <label class="field"><span>Phone</span><input id="gmPhone" inputmode="tel" autocomplete="tel"/></label>
          <label class="field"><span>Email</span><input id="gmEmail" type="email" autocomplete="email"/></label>
          <div class="field field--devices">
            <span>Boards in this group</span>
            <span class="muted" style="font-size:11px;margin-top:-2px;line-height:1.35">Tick boards with no group or already in this one; <strong>Save</strong> applies. Boards already in another group stay locked until moved.</span>
            <div id="gmDevices" class="grp-device-picker" role="group" aria-label="Group member boards"></div>
          </div>
        </div>
        <div class="row grp-modal__actions" style="justify-content:flex-end;gap:8px;margin-top:12px">
          <button class="btn sm secondary" id="gmCancel" type="button">Cancel</button>
          <button class="btn sm" id="gmSave" type="button">Save</button>
        </div>
      </div>
    </div>`;
}

/**
 * Wire group cards surface on #/groups hub directory.
 * @param {HTMLElement} view
 * @param {object} ctx
 * @returns {{ dispose: () => void, renderGroups: () => void }}
 */
export function wireGroupsHubCardsSurface(view, ctx) {
  const {
    routeSeq,
    api,
    toast,
    can,
    $,
    $$,
    setChildMarkup,
    isRouteCurrent,
    scheduleRouteTicker,
    bustDeviceListCaches,
    bustApiGetCachedPrefix,
    canonicalGroupKey,
    displayGroupName,
    groupCardMetaKey,
    parseGroupMetaKey,
    syncGroupMetaWithDevices,
    tryContractApiWithProbe,
    tryGroupDeleteWithProbe,
    putGroupCardSettingsV1,
    applyGroupCardSettingsV1,
    deleteGroupCardV1,
    buildPostGroupCardsApplyPath,
    buildPatchDeviceProfilePath,
    buildPostDevicesBulkProfilePath,
    buildPostAdminNotificationGroupShareLabelPath,
    buildPostAlertsPath,
    buildBulkAlertBody,
    buildPostDeviceSelfTestPath,
    buildPostDeviceCommandsPath,
    runGroupApplyOnAction,
    runGroupDeleteAction,
    buildAuthUsersPath,
    ADMIN_CONSOLE_TIMEOUT_MS,
    buildAdminNotificationGroupSharePreviewPath,
    HOME_CONSOLE_TIMEOUT_MS,
    buildAdminDeviceShareRevokePath,
    fetchGroupShareGrantees,
    granteePermissionSummary,
    syncGroupShareGrantees,
    fetchAllAdminShares,
    grantShareMatrix,
    mountDeviceGridVirtualList,
    DEVICES_GRID_VIRTUAL_MIN,
    OVERVIEW_GROUP_CARD_VIRTUAL_ROW_PX,
    DEFAULT_REMOTE_SIREN_MS,
    getMe,
    getState,
    groupScope,
    initialOv,
    initialList,
    initialGroupSettingsItems,
    showShareChrome,
    buildGroupSlotsFromDeviceList,
    groupTriggerPayloadFromSettings,
  } = ctx;

  let ov = initialOv || { mqtt_connected: false };
  let list = initialList || { items: [] };
  let devices = Array.isArray(list.items) ? list.items.slice() : [];
  let byId = new Map(devices.map((d) => [String(d.device_id), d]));
  const groupSettingsItems = Array.isArray(initialGroupSettingsItems) ? initialGroupSettingsItems : [];

  const GROUP_META_LS_KEY = `croc.group.meta.v2.${groupScope}`;
  const GROUP_SETTINGS_LS_KEY = `croc.group.settings.v1.${groupScope}`;
  const GROUP_API_CAPS_LS_KEY = `croc.group.api.caps.v2.${groupScope}`;
  const loadGroupMeta = () => {
    try {
      const raw = localStorage.getItem(GROUP_META_LS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return (obj && typeof obj === "object") ? obj : {};
    } catch { return {}; }
  };
  const saveGroupMeta = (obj) => localStorage.setItem(GROUP_META_LS_KEY, JSON.stringify(obj || {}));
  const loadLocalGroupSettings = () => {
    try {
      const raw = localStorage.getItem(GROUP_SETTINGS_LS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return (obj && typeof obj === "object") ? obj : {};
    } catch { return {}; }
  };
  const saveLocalGroupSettings = (obj) => localStorage.setItem(GROUP_SETTINGS_LS_KEY, JSON.stringify(obj || {}));
  const loadGroupApiCaps = () => {
    try {
      const raw = localStorage.getItem(GROUP_API_CAPS_LS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return {
        settings: obj && obj.settings === false ? false : true,
        apply: obj && obj.apply === false ? false : true,
        delete: obj && obj.delete === false ? false : true,
        prefix: (obj && (obj.prefix === "/api" || obj.prefix === "")) ? obj.prefix : "",
      };
    } catch {
      return { settings: true, apply: true, delete: true, prefix: "" };
    }
  };
  const saveGroupApiCaps = (caps) => {
    localStorage.setItem(GROUP_API_CAPS_LS_KEY, JSON.stringify({
      settings: !!(caps && caps.settings),
      apply: !!(caps && caps.apply),
      delete: !!(caps && caps.delete),
      prefix: (caps && caps.prefix === "/api") ? "/api" : "",
    }));
  };
  const groupApiCaps = loadGroupApiCaps();
  const groupContractProbeCaps = {
    get prefix() { return groupApiCaps.prefix; },
    onPrefixResolved(px) {
      groupApiCaps.prefix = px;
      saveGroupApiCaps(groupApiCaps);
    },
  };
  const tryContractApiCall = (contractPath, opts) =>
    tryContractApiWithProbe(api, contractPath, opts, groupContractProbeCaps);
  const tryGroupDeleteContractCall = (mode, groupKey, ownerAdmin) =>
    tryGroupDeleteWithProbe(tryContractApiCall, mode, groupKey, ownerAdmin);

  const groupDelayTimers = new Map();
  const localGroupSettings = loadLocalGroupSettings();
  const groupSettingsMap = new Map();
  for (const [k, v] of Object.entries(localGroupSettings)) groupSettingsMap.set(String(k), v || {});
  const meta = loadGroupMeta();
  syncGroupMetaWithDevices(meta, devices);
  saveGroupMeta(meta);

  const groupCardsEl = $("#groupCards", view);
  const grpModalEl = $("#grpModal", view);
  const grpSetModalEl = $("#grpSetModal", view);
  const shareModalEl = $("#shareModal", view);
  if (!groupCardsEl || !grpModalEl || !grpSetModalEl) {
    return { dispose: () => {}, renderGroups: () => {} };
  }

  const me = getMe();
  const groupHtmlKeys = { canonicalGroupKey, displayGroupName, escapeHtml };
  const groupSlotCtx = { me, canonicalGroupKey };
  const groupCardRenderCtx = () => ({
    meta,
    groupSettingsMap,
    selectedGroup: "",
    me,
    canFn: can,
    escapeHtml,
    devices,
    byId,
    canonicalGroupKey,
    displayGroupName,
  });

  let ownerFilterQ = "";
  const devicesForGroups = () => {
    if (!(me && me.role === "superadmin")) return devices;
    const q = ownerFilterQ.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) => String(d.owner_admin || "").toLowerCase().includes(q));
  };
  const groupDeviceIdsFromListFn = (g, tenantOwner) =>
    groupDeviceIdsFromList(devices, g, tenantOwner, groupSlotCtx);
  const inferUniqueOwnerForGroupFn = (gk) =>
    inferUniqueOwnerForGroup(devices, gk, canonicalGroupKey);
  const collectGroupSlots = () => {
    const slotsCtx = { me, canonicalGroupKey, groupCardMetaKey };
    const fromApi = buildOverviewSlotsFromApiResponse(ov, slotsCtx);
    let slots = fromApi != null
      ? mergeSettingsOnlyOverviewSlots(fromApi, groupSettingsItems, slotsCtx)
      : buildGroupSlotsFromDeviceList(devicesForGroups());
    if (me && me.role === "superadmin") {
      slots = filterOverviewSlotsByOwner(slots, ownerFilterQ);
      slots = coalesceOverviewGroupSlots(slots, { me });
    }
    slots = enrichOverviewSlotsForDisplay(slots, devices, slotsCtx, inferUniqueOwnerForGroupFn);
    return slots;
  };
  {
    const slotKeys = new Set(
      collectGroupSlots().map((s) => String(s.metaKey || "")).filter(Boolean),
    );
    for (const x of groupSettingsItems) {
      const gk = String(x.group_key || "");
      const oadm = String(x.owner_admin || "").trim();
      const mkPref = groupCardMetaKey(gk, oadm);
      const mkBare = groupCardMetaKey(gk, "");
      const mk = slotKeys.has(mkPref) ? mkPref : (slotKeys.has(mkBare) ? mkBare : mkPref);
      if (mk) groupSettingsMap.set(mk, x);
    }
  }
  const groupDeviceIdsFromSlotFn = (slot) => groupDeviceIdsFromSlot(slot, devices, byId, groupSlotCtx);
  const sirenTargetIdsForSlotFn = (slot) =>
    sirenTargetIdsForSlot(slot, devices, byId, can("can_alert"), groupSlotCtx);
  const canGroupSirenForSlotFn = (slot) => canGroupSirenForSlot(slot, devices, byId, can, groupSlotCtx);
  const groupSharedBySlotFn = (slot) => groupSharedBySlot(slot, devices, byId, groupSlotCtx);
  const groupSharedByNotificationKeyFn = (gk) =>
    groupSharedByNotificationKey(devices, gk, canonicalGroupKey);
  const buildGroupCardHtml = (slot) => buildOverviewGroupCardHtml(slot, groupCardRenderCtx());
  const groupDeviceRow = (d, opts) => buildOverviewGroupDeviceRowHtml(d, opts, groupHtmlKeys);

  let overviewGroupCardsVirtualDispose = null;
  const renderGroups = () => {
    const slots = collectGroupSlots();
    if (typeof overviewGroupCardsVirtualDispose === "function") {
      try { overviewGroupCardsVirtualDispose(); } catch (_) {}
      overviewGroupCardsVirtualDispose = null;
    }
    const hubPanel = view.querySelector(".grp-hub-directory");
    if (hubPanel) hubPanel.dataset.hubEmpty = slots.length === 0 ? "1" : "0";
    if (slots.length === 0) {
      setChildMarkup(groupCardsEl, `<p class="muted">No groups yet.</p>`);
      return;
    }
    const htmlRows = slots.map((slot) => buildGroupCardHtml(slot));
    if (htmlRows.length < DEVICES_GRID_VIRTUAL_MIN) {
      setChildMarkup(groupCardsEl, htmlRows.join(""));
      return;
    }
    groupCardsEl.innerHTML = "";
    overviewGroupCardsVirtualDispose = mountDeviceGridVirtualList(groupCardsEl, htmlRows, {
      rowPx: OVERVIEW_GROUP_CARD_VIRTUAL_ROW_PX,
    });
  };

  const repopGhOwnerDatalist = () => {
    const dl = $("#ghOwnerDatalist", view);
    if (!dl || !(me && me.role === "superadmin")) return;
    const owners = mergeTenantOwnerDirectory(ov, devices);
    setChildMarkup(dl, owners.map((o) => `<option value="${escapeHtml(o)}"></option>`).join(""));
  };

  const groupModals = createOverviewGroupModalsController({
    view,
    grpModalEl,
    grpSetModalEl,
    api,
    getMe,
    canFn: can,
    toast,
    escapeHtml,
    $,
    $$,
    setChildMarkup,
    getDevices: () => devices,
    devicesForGroups,
    inferUniqueOwnerForGroup: inferUniqueOwnerForGroupFn,
    getById: () => byId,
    getMeta: () => meta,
    saveGroupMeta,
    loadLocalGroupSettings,
    saveLocalGroupSettings,
    getGroupSettingsMap: () => groupSettingsMap,
    groupApiCaps,
    saveGroupApiCaps,
    groupDelayTimers,
    groupCardMetaKey,
    canonicalGroupKey,
    displayGroupName,
    parseGroupMetaKey,
    groupDeviceRow,
    groupDeviceIdsFromList: groupDeviceIdsFromListFn,
    groupDeviceIdsFromSlot: groupDeviceIdsFromSlotFn,
    groupSharedBySlot: groupSharedBySlotFn,
    groupSharedByNotificationKey: groupSharedByNotificationKeyFn,
    sirenTargetIdsForSlot: sirenTargetIdsForSlotFn,
    canGroupSirenForSlot: canGroupSirenForSlotFn,
    tryContractApiCall,
    tryGroupDeleteContractCall,
    putGroupCardSettingsV1,
    applyGroupCardSettingsV1,
    deleteGroupCardV1,
    buildPostGroupCardsApplyPath,
    buildPatchDeviceProfilePath,
    buildPostDevicesBulkProfilePath,
    buildPostAdminNotificationGroupShareLabelPath,
    buildPostAlertsPath,
    buildBulkAlertBody,
    buildPostDeviceSelfTestPath,
    buildPostDeviceCommandsPath,
    runGroupApplyOnAction,
    runGroupDeleteAction,
    bustDeviceListCaches,
    onGroupsChanged: () => renderGroups(),
  });
  const {
    openGroupModal,
    openSettingsModal,
    deleteGroupCard,
    applyGroupSettingsFallback,
    tryApplyGroupV1,
    wireHandlers: wireGroupModalHandlers,
  } = groupModals;
  wireGroupModalHandlers();

  const shareChrome = createOverviewShareChrome({
    view,
    routeSeq,
    shareModalEl,
    api,
    getMe,
    toast,
    escapeHtml,
    $,
    $$,
    setChildMarkup,
    isRouteCurrent,
    getDevices: () => devices,
    groupDeviceIdsFromList: groupDeviceIdsFromListFn,
    inferUniqueOwnerForGroup: inferUniqueOwnerForGroupFn,
    groupDeviceRow,
    canonicalGroupKey,
    buildAuthUsersPath,
    ADMIN_CONSOLE_TIMEOUT_MS,
    buildAdminNotificationGroupSharePreviewPath,
    HOME_CONSOLE_TIMEOUT_MS,
    buildAdminDeviceShareRevokePath,
    fetchGroupShareGrantees,
    granteePermissionSummary,
    syncGroupShareGrantees,
    fetchAllAdminShares,
    grantShareMatrix,
    bustDeviceListCaches,
    canManageShares: () => !!(me && (me.role === "superadmin" || (me.role === "admin" && can("can_manage_users")))),
  });
  const {
    loadShareGrants: loadOverviewShareGrants,
    refreshShareItemsSilently: refreshOverviewShareItemsSilently,
    openShareModal,
    wireHandlers: wireOverviewShareHandlers,
  } = shareChrome;
  wireOverviewShareHandlers();

  $("#grpNew", view).addEventListener("click", () => openGroupModal(""));
  groupCardsEl.addEventListener("click", async (ev) => {
    const btn = ev.target.closest("button");
    if (btn) {
      const slot = readOverviewGroupSlotFromBtn(btn);
      const g = slot.groupKey;
      if (!g) return;
      if (btn.classList.contains("js-edit-group")) {
        openGroupModal(slot.metaKey);
        return;
      }
      if (btn.classList.contains("js-group-settings")) {
        openSettingsModal(slot);
        return;
      }
      if (btn.classList.contains("js-share-group")) {
        if (!(me && (me.role === "superadmin" || (me.role === "admin" && can("can_manage_users"))))) {
          toast("No sharing permission", "err"); return;
        }
        openShareModal(g, slot.tenantOwner, null);
        return;
      }
      if (btn.classList.contains("js-del-group")) {
        if (groupSharedBySlotFn(slot).length > 0) { toast("Shared group cannot be deleted", "err"); return; }
        if (!confirm(`Delete group card "${g}"?`)) return;
        try {
          await deleteGroupCard(g, slot.tenantOwner);
          if (slot.metaKey && meta[slot.metaKey]) delete meta[slot.metaKey];
          saveGroupMeta(meta);
          renderGroups();
          await loadOverviewShareGrants();
          await refreshOverviewShareItemsSilently();
          try { bustDeviceListCaches(); } catch (_) {}
          toast("Group deleted", "ok");
        } catch (e) {
          toast(e.message || e, "err");
        }
        return;
      }
      if (!canGroupSirenForSlotFn(slot)) {
        toast("No alarm permission — need Alert access or Operate on at least one board in this group", "err");
        return;
      }
      const ids = sirenTargetIdsForSlotFn(slot);
      const alarmOwner = resolveGroupAlarmOwnerAdmin(slot, devices, groupSlotCtx, inferUniqueOwnerForGroupFn);
      const serverTotal = slot.serverCounts && Number.isFinite(Number(slot.serverCounts.total))
        ? Number(slot.serverCounts.total)
        : ids.length;
      const action = btn.classList.contains("js-alert-on") ? "on" : "off";
      const confirmN = serverTotal > ids.length ? serverTotal : ids.length;
      if (!confirm(`${action === "on" ? "Open" : "Close"} alarm for ${confirmN} board(s) in ${g}?`)) return;
      try {
        if (action === "on") {
          const payload = groupTriggerPayloadFromSettings(groupSettingsMap.get(slot.metaKey) || {});
          await runGroupApplyOnAction({
            groupKey: g,
            ownerAdmin: alarmOwner,
            payload,
            apiCaps: groupApiCaps,
            saveApiCaps: saveGroupApiCaps,
            tryApplyV1: tryApplyGroupV1,
            tryApplyRoute: (gk, oa) => tryContractApiCall(
              buildPostGroupCardsApplyPath(gk, oa),
              { method: "POST" },
            ),
            applyFallback: applyGroupSettingsFallback,
          });
          toast(`Alarm ON · ${confirmN}`, "ok");
        } else {
          const prevTimer = groupDelayTimers.get(slot.metaKey);
          if (prevTimer) {
            clearTimeout(prevTimer);
            groupDelayTimers.delete(slot.metaKey);
          }
          const res = await api(buildPostAlertsPath(), {
            method: "POST",
            body: buildBulkAlertBody({
              action,
              durationMs: DEFAULT_REMOTE_SIREN_MS,
              notificationGroup: g,
              ownerAdmin: alarmOwner,
              deviceIds: ids,
            }),
          });
          const n = Number(res && res.sent_count);
          if (n === 0 && confirmN) {
            toast("Alarm OFF: none sent. No boards resolved — try again shortly.", "warn");
          } else {
            toast(`Alarm OFF · ${n}`, "ok");
          }
        }
      } catch (e) {
        toast(e.message || e, "err");
      }
      return;
    }
    const card = ev.target.closest(".js-group-card");
    if (!card) return;
    const g = card.dataset.group || "";
    if (!g) return;
    const ow = String(card.getAttribute("data-owner") || "").trim();
    location.hash = `#/group/${encodeURIComponent(g)}${ow ? `?owner=${encodeURIComponent(ow)}` : ""}`;
  });

  let lastHubLivePollToastAt = 0;
  const refreshHubCardsLive = async () => {
    if (!isRouteCurrent(routeSeq)) return;
    try {
      const tick = await fetchOverviewLivePair(api, {
        beforeFetch: () => {
          bustApiGetCachedPrefix("/dashboard/overview");
          bustApiGetCachedPrefix("/devices");
        },
      });
      if (!isRouteCurrent(routeSeq)) return;
      const liveStatus = assessOverviewLiveTick(tick);
      syncOverviewLivePollDegradedBanner(view, liveStatus, "ghLivePollDegradedBanner");
      const toastGate = shouldToastOverviewLivePollFail(lastHubLivePollToastAt, liveStatus);
      if (toastGate.toast) {
        toast(liveStatus.message, "warn");
        lastHubLivePollToastAt = toastGate.at;
      }
      if (tick.ov) ov = tick.ov;
      if (tick.list) {
        list = tick.list;
        devices = Array.isArray(list.items) ? list.items.slice() : [];
        byId = new Map(devices.map((d) => [String(d.device_id), d]));
        syncOverviewFleetPollTruncBanner(view, list, ov, "ghFleetTruncBanner");
      }
      syncOverviewGroupSlotsTruncBanner(view, ov, "ghGroupSlotsTruncBanner");
      const st = getState();
      if (st) st.overviewCache = { ov, list, ts: Date.now() };
      if (tick.list) {
        syncGroupMetaWithDevices(meta, devices);
        saveGroupMeta(meta);
      }
      if (tick.ov || tick.list) repopGhOwnerDatalist();
      renderGroups();
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      const liveStatus = assessOverviewLiveTick({ ov: null, list: null });
      syncOverviewLivePollDegradedBanner(view, liveStatus, "ghLivePollDegradedBanner");
      const toastGate = shouldToastOverviewLivePollFail(lastHubLivePollToastAt, liveStatus);
      if (toastGate.toast) {
        toast(String(e.message || e) || liveStatus.message, "warn");
        lastHubLivePollToastAt = toastGate.at;
      }
    }
  };
  scheduleRouteTicker(routeSeq, "groups-hub-cards-live", refreshHubCardsLive, OVERVIEW_LIVE_MS);

  const ghOwnerInp = $("#ghOwnerFilter", view);
  const ghOwnerClr = $("#ghOwnerClear", view);
  if (ghOwnerInp && me && me.role === "superadmin") {
    repopGhOwnerDatalist();
    const onOwnerFilt = () => {
      ownerFilterQ = String(ghOwnerInp.value || "");
      renderGroups();
    };
    ghOwnerInp.addEventListener("input", onOwnerFilt);
    ghOwnerInp.addEventListener("change", onOwnerFilt);
    if (ghOwnerClr) {
      ghOwnerClr.addEventListener("click", () => {
        ghOwnerInp.value = "";
        ownerFilterQ = "";
        renderGroups();
      });
    }
  }
  renderGroups();
  if (showShareChrome) loadOverviewShareGrants();

  const dispose = () => {
    if (typeof overviewGroupCardsVirtualDispose === "function") {
      try { overviewGroupCardsVirtualDispose(); } catch (_) {}
      overviewGroupCardsVirtualDispose = null;
    }
  };
  return { dispose, renderGroups };
}
